import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Grid from '@mui/material/Grid';
import Stack from '@mui/material/Stack';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SaveIcon from '@mui/icons-material/Save';
import { useNavigate, useParams } from 'react-router';
import { useCallback, useEffect, useMemo, useState } from 'react';
import type { Product } from '../../models/Product';
import { useLazyQuery, useMutation, useQuery } from '@apollo/client/react';
import { GET_PRODUCT_BY_ID_QUERY } from '../../queries/ProductQueries';
import PageContainer from '../layout/PageContainer';
import { Alert, Divider, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography } from '@mui/material';
import NumberField from '../NumberField';
import type { Batch } from '../../models/Batch';
import { CALCULATE_BATCH_MUTATION } from '../../mutations/BatchMutations';
import useNotifications from '../../hooks/useNotifications/useNotifications';
import {
  DataGrid,
  GridColDef,
  GridRowClassNameParams,
  gridClasses,
} from '@mui/x-data-grid';
import { toUSCurrency } from '../../utilities/CurrencyUtilities';
import dayjs from 'dayjs';
import { unitTypeToUnits } from '../../models/Item';
import { ConsumedInventoryItem } from '../../models/ConsumedInventoryItem';
import { GET_TOTAL_ACTIVE_ANNUAL_OVERHEAD_COST } from '../../queries/OverheadCostLineItemQueries';
import { GET_TOTAL_CONSUMABLE_COST } from '../../queries/ConsumableLineItemQueries';
import { ProductBulkPricingMarkupLineItem } from '../../models/ProductBulkPricingMarkupLineItem';
import NumberSpinner from '../NumberSpinner';
import { UPDATE_PRODUCT_BULK_PRICING_MARKUP_LINE_ITEMS } from '../../mutations/ProductMutations';
import { useDialogs } from '../../hooks/useDialogs/useDialogs';

const INITIAL_PAGE_SIZE = 100;

export default function ProductCosting() {
  const { productId } = useParams();
  const navigate = useNavigate();
  const notifications = useNotifications();
  const dialogs = useDialogs();

  const { data: totalActiveAnnualOverheadCostData } = useQuery(
    GET_TOTAL_ACTIVE_ANNUAL_OVERHEAD_COST);

  const { data: totalConsumableCostData } = useQuery(
    GET_TOTAL_CONSUMABLE_COST);

  const [getProductById] = useLazyQuery(
    GET_PRODUCT_BY_ID_QUERY);

  const [calculateBatchForProduct] = useMutation(
    CALCULATE_BATCH_MUTATION);

  const [updatedBulkPricingMarkupLineItems] = useMutation(
    UPDATE_PRODUCT_BULK_PRICING_MARKUP_LINE_ITEMS);

  const [product, setProduct] = useState<Product | null>(null);
  const [bulkPricingMarkupLineItems, setBulkPricingMarkupLineItems] = useState<ProductBulkPricingMarkupLineItem[]>([]);
  const [bulkPricingMarkupLineItemsDirty, setBulkPricingMarkupLineItemsDirty] = useState(false);
  const [bulkPricingMarkupLineItemsUpdating, setBulkPricingMarkupLineItemsUpdating] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isCalculatingBatch, setIsCalculatingBatch] = useState(false);
  const [calculatedBatch, setCalculatedBatch] = useState<Batch | null>(null);
  const [amount, setAmount] = useState<number>(0);
  const [calculatedAmount, setCalculatedAmount] = useState<number>(0);
  const [error, setError] = useState<Error | null>(null);

  const loadData = useCallback(async () => {
    setError(null);

    const id = +(productId || '0');

    if (id > 0) {
      setIsLoading(true);

      try {
        const { data, error: getProductByIdError } = await getProductById(
          { variables: { id } }
        );

        setIsLoading(false);
        setError(null);

        if (getProductByIdError) {
          setError(getProductByIdError);
        }

        if (data?.product) {
          setProduct(data.product);

          if (data.product.bulkPricingMarkupLineItems) {
            setBulkPricingMarkupLineItems(data.product.bulkPricingMarkupLineItems);
          }
        } else {
          setError({ message: 'No product found' } as Error);
        }
      } catch (showDataError) {
        setError(showDataError as Error);
      }
    }
    setIsLoading(false);
  }, [getProductById, productId]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const onAmountChange = useCallback(
    async (value: number | null, fieldName: string) => {
      setAmount(value || 0);
    },
    [],
  );

  const calculateBatch = useCallback(async () => {
    if ((amount || 0) > 0) {
      setIsCalculatingBatch(true);

      const { data: result } = await calculateBatchForProduct({
        variables: {
          productId: +(productId || '0'),
          amount: amount
        }
      });

      setIsCalculatingBatch(false);

      if (result?.batch) {
        setCalculatedBatch(result.batch);
        setCalculatedAmount(amount);
      } else {
        notifications.show(
          'Failed to calculate product batch. Reason: Unknown.',
          {
            severity: 'error',
            autoHideDuration: 3000,
          },
        );
      }
    }
  }, [amount, calculateBatchForProduct, notifications, productId]);

  const handleBack = useCallback(() => {
    navigate(`/dashboard/products/${productId}`);
  }, [navigate, productId]);

  const onMarkupChange = useCallback(
    async (bulkPricingMarkupLineItemId: number, value: number | null) => {
      setBulkPricingMarkupLineItems(prevBulkPricingMarkupLineItems =>
        prevBulkPricingMarkupLineItems.map(bulkPricingMarkupLineItem =>
          bulkPricingMarkupLineItem.id === bulkPricingMarkupLineItemId ?
            { ...bulkPricingMarkupLineItem, markup: value || 0 } :
            bulkPricingMarkupLineItem
        ));

      setBulkPricingMarkupLineItemsDirty(true);
    }, []);

  const saveBulkPricingMarkupLineItems = useCallback(async () => {

    if (await dialogs.confirm('Are you sure you want to update pricing?')) {
      setBulkPricingMarkupLineItemsUpdating(true);

      const { data: result } = await updatedBulkPricingMarkupLineItems({
        variables: {
          updatedBulkPricingMarkupLineItems: bulkPricingMarkupLineItems.map((bulkPricingMarkupLineItem) => ({
            id: bulkPricingMarkupLineItem.id,
            productId: bulkPricingMarkupLineItem.productId,
            pounds: bulkPricingMarkupLineItem.pounds,
            markup: bulkPricingMarkupLineItem.markup
          })),
        }
      });

      setBulkPricingMarkupLineItemsUpdating(false);

      if (result?.updated) {
        notifications.show(
          'Bulk pricing markup line items updated.',
          {
            severity: 'success',
            autoHideDuration: 3000,
          });
      } else {
        notifications.show(
          'Could not updated bulk pricing markup line items.',
          {
            severity: 'error',
            autoHideDuration: 3000,
          });
      }
    }
  }, [bulkPricingMarkupLineItems, dialogs, notifications, updatedBulkPricingMarkupLineItems]);

  const totalIngredientCostPerPound = useCallback(() =>
    (calculatedBatch?.totalCost || 0) / calculatedAmount,
    [calculatedAmount, calculatedBatch?.totalCost]);

  const totalOverheadCostPerPound = useMemo(() =>
    (totalActiveAnnualOverheadCostData?.totalActiveAnnualOverheadCost || 0.00) / 2000,
    [totalActiveAnnualOverheadCostData]);

  const totalLaborCostPerPound = useMemo(() =>
    ((calculatedAmount / 14) * 40.0) / calculatedAmount,
    [calculatedAmount]);

  const totalConsumableCostPerPound = useMemo(() =>
    (totalConsumableCostData?.totalConsumableCost || 0.00) / calculatedAmount,
    [calculatedAmount, totalConsumableCostData?.totalConsumableCost]);

  const totalNonIngredientCostPerPound = useMemo(() =>
    totalOverheadCostPerPound + totalLaborCostPerPound + totalConsumableCostPerPound,
    [totalConsumableCostPerPound, totalLaborCostPerPound, totalOverheadCostPerPound]);

  const totalCostPerPound = useMemo(() =>
    totalIngredientCostPerPound() + totalNonIngredientCostPerPound,
    [totalIngredientCostPerPound, totalNonIngredientCostPerPound]);

  const salePrice = useMemo(() => {
    if ((bulkPricingMarkupLineItems || []).length === 3) {
      if (calculatedAmount < bulkPricingMarkupLineItems[1].pounds) {
        return totalCostPerPound * (bulkPricingMarkupLineItems[0].markup / 100) * calculatedAmount;
      }

      if (calculatedAmount < bulkPricingMarkupLineItems[2].pounds) {
        return totalCostPerPound * (bulkPricingMarkupLineItems[1].markup / 100) * calculatedAmount;
      }

      return totalCostPerPound * (bulkPricingMarkupLineItems[2].markup / 100) * calculatedAmount;
    }
  }, [bulkPricingMarkupLineItems, calculatedAmount, totalCostPerPound]);

  const columns = useMemo<GridColDef[]>(
    () => [
      {
        field: 'itemName',
        headerName: 'Item',
        flex: .50,
        display: 'flex',
        valueGetter: (_, row) => row.item?.name
      },
      {
        field: 'productBatchQuantity',
        headerName: 'Qty Needed (lbs)',
        type: 'number',
        flex: 1,
        display: 'flex',
        valueFormatter: (value: number | null) => (value || 0).toFixed(3)
      },
      {
        field: 'remainingProductBatchQuantity',
        headerName: 'Rem Qty Needed (lbs)',
        type: 'number',
        flex: 1,
        display: 'flex',
        valueFormatter: (value: number | null) => (value || 0).toFixed(3)
      },
      {
        field: 'calculatedItemCost',
        headerName: 'Total Item Cost ($)',
        type: 'number',
        flex: 1,
        display: 'flex',
        valueFormatter: (value: number | null) => toUSCurrency(value || 0)
      },
      {
        field: 'calculatedInventoryItems',
        headerName: 'Calculated Inventory Items',
        sortable: false,
        flex: 5,
        minWidth: 600,
        renderCell: (params) => {
          return (
            (params.row.calculatedConsumedInventoryItems || []).length === 0 ?
              <Box sx={{ color: '#fac1c0' }}>
                No items available.
              </Box> : (
                <Table sx={{ minWidth: 600 }} aria-label="dense table" size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontSize: '85%', paddingLeft: 0, width: '16%' }} component="th" scope="row" align="left">Vendor</TableCell>
                      <TableCell sx={{ fontSize: '85%', paddingLeft: 0, width: '14%' }} component="th" scope="row" align="left">Lot #</TableCell>
                      <TableCell sx={{ fontSize: '85%', paddingLeft: 0, width: '14%' }} component="th" scope="row" align="left">Date Ordered</TableCell>
                      <TableCell sx={{ fontSize: '85%', paddingLeft: 0, width: '14%' }} component="th" scope="row" align="right">QOH</TableCell>
                      <TableCell sx={{ fontSize: '85%', paddingLeft: 0, width: '14%' }} component="th" scope="row" align="right">Qty Used</TableCell>
                      <TableCell sx={{ fontSize: '85%', paddingLeft: 0, width: '14%' }} component="th" scope="row" align="right">ROH</TableCell>
                      <TableCell sx={{ fontSize: '85%', paddingLeft: 0, width: '14%' }} component="th" scope="row" align="right">Total Cost</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(params.row.calculatedConsumedInventoryItems || []).map((consumedItem: ConsumedInventoryItem) => (
                      <TableRow
                        key={consumedItem.inventoryItem?.id}
                        sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                      >
                        <TableCell sx={{ fontSize: '85%', paddingLeft: 0, width: '16%' }} align="left">
                          {consumedItem.inventoryItem?.vendor?.name}
                        </TableCell>
                        <TableCell sx={{ fontSize: '85%', paddingLeft: 0, width: '14%' }} align="left">
                          {consumedItem.inventoryItem?.lotNumber || 'N/A'}
                        </TableCell>
                        <TableCell sx={{ fontSize: '85%', paddingLeft: 0, width: '14%' }} align="left">
                          {dayjs(consumedItem.inventoryItem?.purchaseOrder?.dateTimePurchased).format('M/D/YYYY') || 'N/A'}
                        </TableCell>
                        <TableCell sx={{ fontSize: '85%', paddingLeft: 0, width: '14%' }} align="right">
                          {(consumedItem.inventoryItem?.quantityOnHand ?? 0).toFixed(3)} {unitTypeToUnits(consumedItem.inventoryItem?.item?.unitType)}
                        </TableCell>
                        <TableCell sx={{ fontSize: '85%', paddingLeft: 0, width: '14%' }} align="right">
                          {consumedItem.quantityUsed?.toFixed(3)} {unitTypeToUnits(consumedItem.inventoryItem?.item?.unitType)}
                        </TableCell>
                        <TableCell sx={{ fontSize: '85%', paddingLeft: 0, width: '14%' }} align="right">
                          {consumedItem.inventoryItem?.remainingQuantityOnHand?.toFixed(3)} {unitTypeToUnits(consumedItem.inventoryItem?.item?.unitType)}
                        </TableCell>
                        <TableCell sx={{ fontSize: '85%', paddingLeft: 0, width: '14%' }} align="right">
                          ${toUSCurrency(consumedItem.totalItemCost)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )
          );
        }
      }
    ],
    [],
  );

  const getRowClassName = (params: GridRowClassNameParams) => {
    if (!params.row.productBatchQuantityMet) {
      return 'product-batch-quantity-not-met';
    }

    return '';
  }

  const getMarkupPounds = useCallback(
    (index: number) => {

      if (bulkPricingMarkupLineItems.length === 3) {
        switch (index) {
          case 0: {
            return `0 - ${bulkPricingMarkupLineItems[1].pounds - 1}`;
          }

          case 1: {
            return `${bulkPricingMarkupLineItems[1].pounds} - ${bulkPricingMarkupLineItems[2].pounds - 1}`;
          }

          case 2: {
            return `${bulkPricingMarkupLineItems[2].pounds}+`;
          }
        }

        return bulkPricingMarkupLineItems[index].pounds;
      }
    }, [bulkPricingMarkupLineItems]);

  if (isLoading) {
    return (
      <Box
        sx={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          width: '100%',
          m: 1,
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ flexGrow: 1 }}>
        <Alert severity="error">{error.message}</Alert>
      </Box>
    );
  }

  const pageTitle = `${product?.name} Costing`;

  return (
    <PageContainer
      title={pageTitle}
      breadcrumbs={[
        { title: 'Products', path: '/dashboard/products' },
        { title: `${product?.name}`, path: `/dashboard/products/${product?.id}` },
        { title: 'Costing' }
      ]}
    >
      <Box
        sx={{ width: '100%' }}
      >
        <Grid container spacing={1} sx={{ mb: 2, width: '100%' }}>
          <Box sx={{ width: '100%' }}>
            <Grid container>
              <Box sx={{ width: '20%' }}>
                <Stack direction="row" spacing={2} justifyContent="space-between">
                  <NumberField
                    value={amount || null}
                    onValueChange={(event) => onAmountChange(event, 'amount')}
                    name="amount"
                    label="Amount"
                    error={amount === undefined || amount <= .1 || amount > 1000}
                    helperText={amount === undefined || amount <= .1 || amount > 1000 ? 'Please enter a valid amount.' : ' '}
                    weightFieldUnits="lbs"
                    fullWidth
                  />
                  <Box sx={{ paddingTop: .75 }}>
                    <Button
                      type="button"
                      variant="contained"
                      size="medium"
                      disabled={amount === undefined || amount <= .1 || amount > 1000}
                      loading={isCalculatingBatch}
                      onClick={calculateBatch}
                    >
                      Calculate
                    </Button>
                  </Box>
                </Stack>
              </Box>
              {calculatedBatch ?
                <Box sx={{ width: '80%' }}>
                  <Stack direction="row" justifyContent="right" spacing={2} sx={{ width: '100%' }}>
                    <Box sx={{ fontSize: '20px', pt: 1 }}>
                      <strong>Total Ingredient Cost:</strong> ${calculatedBatch ? toUSCurrency(calculatedBatch.totalCost) : '0.00'}
                    </Box>
                    <Box sx={{ fontSize: '20px', pt: 1 }}>
                      <strong>Total Production Cost:</strong> ${calculatedBatch ? toUSCurrency(totalNonIngredientCostPerPound * calculatedAmount) : '0.00'}
                    </Box>
                    <Box sx={{ fontSize: '20px', pt: 1 }}>
                      <strong>Total Cost:</strong> ${calculatedBatch ? toUSCurrency((calculatedBatch.totalCost || 0) + (totalNonIngredientCostPerPound * calculatedAmount)) : '0.00'}
                    </Box>
                    <Box sx={{ pt: 1 }}>
                      <Typography color="success" fontSize="20px">
                        <strong>Total Sale Price:</strong> ${toUSCurrency(salePrice)}
                      </Typography>
                    </Box>
                  </Stack>
                </Box> : <></>
              }
            </Grid>
          </Box>
          {calculatedBatch ?
            <Box sx={{ height: '25vh', width: '100%' }}>
              <DataGrid
                density="compact"
                rows={calculatedBatch.calculatedProductItems}
                columns={columns}
                loading={isLoading}
                pageSizeOptions={[INITIAL_PAGE_SIZE]}
                getRowHeight={() => 'auto'}
                getRowClassName={getRowClassName}
                sx={[
                  () => ({
                    '&.MuiDataGrid-root--densityCompact .MuiDataGrid-cell': { py: '8px' },
                    '&.MuiDataGrid-root--densityStandard .MuiDataGrid-cell': { py: '15px' },
                    '&.MuiDataGrid-root--densityComfortable .MuiDataGrid-cell': { py: '22px' },
                    [`& .${gridClasses.columnHeader}, & .${gridClasses.cell}`]: {
                      outline: 'transparent',
                    },
                    [`& .${gridClasses.columnHeader}:focus-within, & .${gridClasses.cell}:focus-within`]:
                    {
                      outline: 'none',
                    },
                    [`& .${gridClasses.row}:hover`]: {
                      cursor: 'pointer',
                    },
                    '.product-batch-quantity-not-met': {
                      backgroundColor: '#f8d7c0',
                      '&:hover': {
                        backgroundColor: 'rgba(248,215,192,.75)',
                        filter: 'brightness(105%)'
                      }
                    }
                  }),
                  (theme) =>
                    theme.applyStyles('dark', {
                      '.product-batch-quantity-not-met': {
                        backgroundColor: '#ed201c',
                        '&:hover': {
                          backgroundColor: '#ed201c',
                          filter: 'brightness(120%)'
                        }
                      }
                    })]}
                slotProps={{
                  loadingOverlay: {
                    variant: 'circular-progress',
                    noRowsVariant: 'circular-progress',
                  },
                  baseIconButton: {
                    size: 'small',
                  },
                }}
              />
            </Box>
            : <></>
          }
        </Grid>
        {calculatedBatch ?
          <Stack direction="row" spacing={2} justifyContent="space-between" sx={{ width: '100%', mb: 2 }}>
            <Box sx={{ width: '50%' }}>
              <TableContainer sx={{ maxHeight: 250 }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontSize: '85%', paddingLeft: 0 }} component="th" scope="row" align="left">Description</TableCell>
                      <TableCell sx={{ fontSize: '85%', paddingLeft: 0 }} component="th" scope="row" align="right">Cost per Pound ($/lb)</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    <TableRow>
                      <TableCell sx={{ fontSize: '85%', paddingLeft: 0 }} align="left">Ingredients</TableCell>
                      <TableCell sx={{ fontSize: '85%', paddingLeft: 0 }} align="right">{toUSCurrency(totalIngredientCostPerPound())}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell sx={{ fontSize: '85%', paddingLeft: 0 }} align="left">Overhead</TableCell>
                      <TableCell sx={{ fontSize: '85%', paddingLeft: 0 }} align="right">{toUSCurrency(totalOverheadCostPerPound)}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell sx={{ fontSize: '85%', paddingLeft: 0 }} align="left">Labor</TableCell>
                      <TableCell sx={{ fontSize: '85%', paddingLeft: 0 }} align="right">{toUSCurrency(totalLaborCostPerPound)}</ TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell sx={{ fontSize: '85%', paddingLeft: 0 }} align="left">Consumables</TableCell>
                      <TableCell sx={{ fontSize: '85%', paddingLeft: 0 }} align="right">{toUSCurrency(totalConsumableCostPerPound)}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell sx={{ fontSize: '85%', paddingLeft: 0 }} align="left"><strong>Total</strong></TableCell>
                      <TableCell sx={{ fontSize: '85%', paddingLeft: 0 }} align="right">{toUSCurrency(totalCostPerPound)}</TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
            <Divider orientation="vertical" flexItem />
            <Box sx={{ width: '50%' }}>
              <TableContainer sx={{ maxHeight: 250 }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontSize: '85%', paddingLeft: 0 }} component="th" scope="row" align="right">Pounds (lbs)</TableCell>
                      <TableCell sx={{ fontSize: '85%', paddingLeft: 0 }} component="th" scope="row" align="right">Markup (%)</TableCell>
                      <TableCell sx={{ fontSize: '85%', paddingLeft: 0 }} component="th" scope="row" align="right">Markup Price ($)</TableCell>
                      <TableCell sx={{ fontSize: '85%', paddingLeft: 0 }} component="th" scope="row" align="right">Markup Per Pound ($/lb)</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {bulkPricingMarkupLineItems?.map((bulkPricingMarkupLineItem: ProductBulkPricingMarkupLineItem, index: number) => (
                      <TableRow key={bulkPricingMarkupLineItem.id}>
                        <TableCell sx={{ fontSize: '85%', paddingLeft: 0 }} align="right">
                          {getMarkupPounds(index)}
                        </TableCell>
                        <TableCell sx={{ fontSize: '85%', paddingLeft: 0 }} align="right">
                          <NumberSpinner
                            size="small"
                            min={0}
                            max={300}
                            value={bulkPricingMarkupLineItem.markup}
                            onValueChange={(value) => onMarkupChange(bulkPricingMarkupLineItem.id, value)} />
                        </TableCell>
                        <TableCell sx={{ fontSize: '85%', paddingLeft: 0 }} align="right">{toUSCurrency((totalCostPerPound * (bulkPricingMarkupLineItem.markup / 100) * bulkPricingMarkupLineItem.pounds))}</TableCell>
                        <TableCell sx={{ fontSize: '85%', paddingLeft: 0 }} align="right">{toUSCurrency(((totalCostPerPound * (bulkPricingMarkupLineItem.markup / 100) * bulkPricingMarkupLineItem.pounds) / bulkPricingMarkupLineItem.pounds))}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              <Stack direction="row" spacing={2} justifyContent="right" sx={{ mt: 2 }}>
                <Button
                  variant="contained"
                  color="success"
                  size="small"
                  startIcon={<SaveIcon />}
                  loading={bulkPricingMarkupLineItemsUpdating}
                  disabled={!bulkPricingMarkupLineItemsDirty}
                  onClick={saveBulkPricingMarkupLineItems}
                >
                  Save
                </Button>
              </Stack>
            </Box>
          </Stack> : <></>
        }
        <Stack direction="row" spacing={2} justifyContent="space-between">
          <Button
            variant="contained"
            startIcon={<ArrowBackIcon />}
            onClick={handleBack}
          >
            Back
          </Button>
          <></>
        </Stack>
      </Box>
    </PageContainer >
  );
}
